// TypeScript interfaces specific to chat components

export interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  question?: string; // For assistant messages, stores the user's question
  aiProvider?: 'groq' | 'openai'; // For assistant messages, which AI provider was used
  fallbackReason?: string | null; // If fallback was used, why
}

export interface ChatState {
  messages: Message[];
  isLoading: boolean;
  error: string | null;
  isStreaming?: boolean;
  connectionStatus?: 'connected' | 'connecting' | 'disconnected' | 'error';
  currentAIProvider?: 'groq' | 'openai'; // Track current AI provider for visual indicator
}

export interface ChatInterfaceProps {
  className?: string;
  placeholder?: string;
  onMessageSent?: (message: string) => void;
  initialMessages?: Message[];
  onNavigateToTerms?: () => void;
  onNavigateToPrivacy?: () => void;
}

export interface MessageProps {
  message: Message;
  isLatest?: boolean;
  showAvatar?: boolean;
}

export interface MessageListProps {
  messages: Message[];
  isLoading: boolean;
  currentAIProvider?: 'groq' | 'openai';
  lastFallbackReason?: string | null;
}

// Speaking tone options
export type SpeakingTone = 'professional' | 'friendly' | 'concise' | 'consultative';

// Tone option structure for dropdown
export interface ToneOption {
  value: SpeakingTone;
  label: string;
  description: string;
}

// Tone options configuration
export const TONE_OPTIONS: ToneOption[] = [
  {
    value: 'professional',
    label: 'Professional & Formal',
    description: 'Structured, authoritative responses suitable for formal presentations'
  },
  {
    value: 'friendly', 
    label: 'Conversational & Friendly',
    description: 'Warm, approachable responses that build rapport'
  },
  {
    value: 'concise',
    label: 'Direct & Concise', 
    description: 'Brief, focused responses that get straight to the point'
  },
  {
    value: 'consultative',
    label: 'Consultative & Detailed',
    description: 'Thorough explanations with comprehensive context'
  }
];

export interface MessageInputProps {
  onSendMessage: (message: string, tone?: SpeakingTone) => void;
  isLoading: boolean;
  placeholder?: string;
}

export interface TypingIndicatorProps {
  visible: boolean;
}